import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import DateRangeFilter from "../../common/DateRangeFilter";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { useUserData } from "../../../hooks/useUserData";
import { fetchMyLearningsHub } from "./qmsService";
import {
  getApiTable,
  getApiTableRow,
  getPaginationInfo,
  isApiSuccess,
  apiMessage,
} from "./qmsApiUtils";
import { getQmsAccessState } from "./qmsPermissionUtils";
import { getQmsScreen } from "./qmsScreenConfig";
import QmsMyLearningsWorkspaceModal from "./QmsMyLearningsWorkspaceModal";
import {
  MY_LEARNINGS_STATUS_FILTER_OPTIONS,
  getMyLearningDocumentCode,
  getMyLearningUnifiedStatusMeta,
  parseDocumentIdNumberSearch,
} from "./qmsMyLearningsUtils";
import { formatReviewDateCell } from "./qmsReviewDateUtils";
import "./QmsPages.scss";

const DEFAULT_PAGE_SIZE = 25;
const screen = getQmsScreen("myLearnings");

const MY_LEARNINGS_SERVER_SEARCH_FIELDS = [
  "DocumentId",
  "DocumentTitle",
  "LearningStatus",
];

const EMPTY_DATE_RANGE = {
  preset: "All Time",
  startDate: "",
  endDate: "",
};

const COLUMN_DATE_PRESETS = [
  "Last 30 Days",
  "Last 90 Days",
  "This Year",
  "All Time",
  "Custom",
];

const FIELD_OPTIONS = [
  { key: "DocumentId", label: "ID" },
  { key: "DocumentTitle", label: "Document" },
  { key: "LearningStatus", label: "Status" },
  { key: "ReviewDate", label: "Review Date" },
  { key: "IsReadingMandatory", label: "Mandatory" },
  { key: "IsQuizApplicable", label: "Quiz" },
  { key: "PublishedOn", label: "Published" },
  { key: "CompletedOn", label: "Completed" },
];

const DEFAULT_COLUMNS = [
  "DocumentId",
  "DocumentTitle",
  "LearningStatus",
  "ReviewDate",
  "IsReadingMandatory",
  "IsQuizApplicable",
  "PublishedOn",
  "CompletedOn",
];

function formatYesNo(value) {
  const yes = Number(value) === 1 || value === true;
  return (
    <span className={`qms-doc-lib__yesno ${yes ? "is-yes" : "is-no"}`}>
      {yes ? "Yes" : "No"}
    </span>
  );
}

function parseYesNoFilter(val) {
  const v = String(val || "").trim().toLowerCase();
  if (!v) return null;
  if (v === "1" || v === "yes") return true;
  if (v === "0" || v === "no") return false;
  return null;
}

function QmsMyLearnings() {
  const { userData, loading: userLoading } = useUserData();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const organizationId = userData?.OrganizationId;
  const staffId = userData?.StaffId;

  const accessLearning = getQmsAccessState({
    userData,
    isSystemAdmin: userData?.Role === "System Administrator",
    permissionKey: "QMS_MY_LEARNING",
    loading: userLoading,
  });

  const accessRecords = getQmsAccessState({
    userData,
    isSystemAdmin: userData?.Role === "System Administrator",
    permissionKey: "QMS_MY_RECORDS",
    loading: userLoading,
  });

  const access =
    accessLearning.status === "allowed" || accessRecords.status === "allowed"
      ? { status: "allowed" }
      : accessLearning.status === "loading" || accessRecords.status === "loading"
        ? { status: "loading" }
        : accessLearning;

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [titleSearch, setTitleSearch] = useState("");
  const [learningStatusFilter, setLearningStatusFilter] = useState("");
  const [readingMandatoryFilter, setReadingMandatoryFilter] = useState("");
  const [quizApplicableFilter, setQuizApplicableFilter] = useState("");
  const [filterDocumentIdNumber, setFilterDocumentIdNumber] = useState("");
  const [reviewDateRange, setReviewDateRange] = useState(EMPTY_DATE_RANGE);
  const [publishedDateRange, setPublishedDateRange] = useState(EMPTY_DATE_RANGE);
  const [completedDateRange, setCompletedDateRange] = useState(EMPTY_DATE_RANGE);
  const [tableLoading, setTableLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [workspaceRow, setWorkspaceRow] = useState(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const fetchSeqRef = useRef(0);
  const userDataRef = useRef(userData);
  const showAlertRef = useRef(showAlert);
  userDataRef.current = userData;
  showAlertRef.current = showAlert;

  const notifyError = useCallback((message) => {
    showAlertRef.current({ title: "Error", message, type: "danger" });
  }, []);

  const notifySuccess = useCallback((message) => {
    showAlertRef.current({ title: "Success", message, type: "success" });
  }, []);

  const handleServerColumnSearch = useCallback((columnSearches) => {
    setPageNumber(1);
    setFilterDocumentIdNumber(parseDocumentIdNumberSearch(columnSearches.DocumentId));
    setTitleSearch(String(columnSearches.DocumentTitle || "").trim());
  }, []);

  const handleLearningStatusFilter = useCallback((value) => {
    setPageNumber(1);
    setLearningStatusFilter(value);
  }, []);

  const handleReadingMandatoryFilter = useCallback((value) => {
    setPageNumber(1);
    setReadingMandatoryFilter(value);
  }, []);

  const handleQuizApplicableFilter = useCallback((value) => {
    setPageNumber(1);
    setQuizApplicableFilter(value);
  }, []);

  const handleReviewDateRangeChange = useCallback((range) => {
    setReviewDateRange(range);
    setPageNumber(1);
  }, []);

  const handlePublishedDateRangeChange = useCallback((range) => {
    setPublishedDateRange(range);
    setPageNumber(1);
  }, []);

  const handleCompletedDateRangeChange = useCallback((range) => {
    setCompletedDateRange(range);
    setPageNumber(1);
  }, []);

  const filterReviewDueDateFrom =
    reviewDateRange?.preset === "All Time" ? "" : reviewDateRange?.startDate || "";
  const filterReviewDueDateTo =
    reviewDateRange?.preset === "All Time" ? "" : reviewDateRange?.endDate || "";
  const filterPublishedOnFrom =
    publishedDateRange?.preset === "All Time" ? "" : publishedDateRange?.startDate || "";
  const filterPublishedOnTo =
    publishedDateRange?.preset === "All Time" ? "" : publishedDateRange?.endDate || "";
  const filterCompletedOnFrom =
    completedDateRange?.preset === "All Time" ? "" : completedDateRange?.startDate || "";
  const filterCompletedOnTo =
    completedDateRange?.preset === "All Time" ? "" : completedDateRange?.endDate || "";

  useEffect(() => {
    if (access.status !== "allowed" || !organizationId || !staffId) {
      return undefined;
    }

    let cancelled = false;
    const seq = ++fetchSeqRef.current;

    const load = async () => {
      if (!hasLoadedOnce) {
        setTableLoading(true);
      }
      try {
        const response = await fetchMyLearningsHub(userDataRef.current, {
          pageNumber,
          pageSize,
          searchText: titleSearch,
          learningStatus: learningStatusFilter || "All",
          filterDocumentIdNumber,
          filterReviewDueDateFrom,
          filterReviewDueDateTo,
          filterPublishedOnFrom,
          filterPublishedOnTo,
          filterCompletedOnFrom,
          filterCompletedOnTo,
          isReadingMandatory: parseYesNoFilter(readingMandatoryFilter),
          isQuizApplicable: parseYesNoFilter(quizApplicableFilter),
        });
        if (cancelled || seq !== fetchSeqRef.current) return;

        if (!isApiSuccess(response)) {
          throw new Error(apiMessage(response) || "Failed to load My Learnings.");
        }

        setRows(getApiTable(response, "MyLearningDocuments"));
        setSummary(getApiTableRow(response, "MyLearningsSummary") || {});
        const pagination = getPaginationInfo(response);
        setTotalCount(Number(pagination.TotalCount) || 0);
        setHasLoadedOnce(true);
      } catch (error) {
        if (cancelled || seq !== fetchSeqRef.current) return;
        showAlertRef.current({
          title: "Error",
          message: error?.message || "Failed to load My Learnings.",
          type: "danger",
        });
        setRows([]);
        setSummary({});
        setTotalCount(0);
      } finally {
        if (!cancelled && seq === fetchSeqRef.current) {
          setTableLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    access.status,
    organizationId,
    staffId,
    pageNumber,
    pageSize,
    titleSearch,
    learningStatusFilter,
    filterDocumentIdNumber,
    filterReviewDueDateFrom,
    filterReviewDueDateTo,
    filterPublishedOnFrom,
    filterPublishedOnTo,
    filterCompletedOnFrom,
    filterCompletedOnTo,
    readingMandatoryFilter,
    quizApplicableFilter,
    refreshToken,
  ]);

  const statCards = useMemo(
    () => [
      { label: "Assigned", value: summary.TotalAssigned ?? 0, tone: "neutral" },
      { label: "To do", value: summary.PendingCount ?? 0, tone: "warning" },
      { label: "Completed", value: summary.CompletedCount ?? 0, tone: "success" },
      { label: "Quiz pending", value: summary.QuizPendingCount ?? 0, tone: "info" },
    ],
    [summary],
  );

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "DocumentId") {
        if (options?.checkOnly) return true;
        return (
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className="report-table__column-search qms-doc-lib__column-filter"
            placeholder="Search number"
            value={parseDocumentIdNumberSearch(options?.value)}
            disabled={options?.disabled}
            onChange={(event) => {
              options?.onChange?.(parseDocumentIdNumberSearch(event.target.value));
            }}
          />
        );
      }

      if (field === "LearningStatus") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search qms-doc-lib__column-filter qms-doc-lib__column-select"
            value={learningStatusFilter}
            disabled={options?.disabled}
            onChange={(event) => handleLearningStatusFilter(event.target.value)}
          >
            {MY_LEARNINGS_STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      }

      if (field === "IsReadingMandatory") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search qms-doc-lib__column-filter qms-doc-lib__column-select"
            value={readingMandatoryFilter}
            disabled={options?.disabled}
            onChange={(event) => handleReadingMandatoryFilter(event.target.value)}
            aria-label="Filter by mandatory reading"
          >
            <option value="">All</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
        );
      }

      if (field === "IsQuizApplicable") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search qms-doc-lib__column-filter qms-doc-lib__column-select"
            value={quizApplicableFilter}
            disabled={options?.disabled}
            onChange={(event) => handleQuizApplicableFilter(event.target.value)}
            aria-label="Filter by quiz applied"
          >
            <option value="">All</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
        );
      }

      if (field === "ReviewDate") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-date-filter">
            <DateRangeFilter
              value={reviewDateRange}
              onChange={handleReviewDateRangeChange}
              placeholder="Review due range"
              presetOptions={COLUMN_DATE_PRESETS}
              compact
            />
          </div>
        );
      }

      if (field === "PublishedOn") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-date-filter">
            <DateRangeFilter
              value={publishedDateRange}
              onChange={handlePublishedDateRangeChange}
              placeholder="Published range"
              presetOptions={COLUMN_DATE_PRESETS}
              compact
            />
          </div>
        );
      }

      if (field === "CompletedOn") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-date-filter">
            <DateRangeFilter
              value={completedDateRange}
              onChange={handleCompletedDateRangeChange}
              placeholder="Completed range"
              presetOptions={COLUMN_DATE_PRESETS}
              compact
            />
          </div>
        );
      }

      return null;
    },
    [
      completedDateRange,
      handleCompletedDateRangeChange,
      handleLearningStatusFilter,
      handlePublishedDateRangeChange,
      handleQuizApplicableFilter,
      handleReadingMandatoryFilter,
      handleReviewDateRangeChange,
      learningStatusFilter,
      quizApplicableFilter,
      readingMandatoryFilter,
      publishedDateRange,
      reviewDateRange,
    ],
  );

  const renderCell = useCallback((row, field) => {
    if (field === "DocumentId") {
      return (
        <span className="qms-doc-lib__doc-id" title={getMyLearningDocumentCode(row)}>
          {getMyLearningDocumentCode(row)}
        </span>
      );
    }
    if (field === "DocumentTitle") {
      const title = String(row.DocumentTitle || "—");
      return (
        <button
          type="button"
          className="report-table__cell-link qms-doc-lib__doc-title-link"
          onClick={() => setWorkspaceRow(row)}
        >
          {title}
        </button>
      );
    }
    if (field === "LearningStatus") {
      const meta = getMyLearningUnifiedStatusMeta(row);
      return (
        <span className={`qms-my-learnings-badge is-${meta.tone}`}>{meta.label}</span>
      );
    }
    if (field === "ReviewDate") {
      const review = formatReviewDateCell(row);
      return (
        <div className={`qms-doc-lib__review-cell is-${review.status || "none"}`}>
          <div className="qms-doc-lib__review-line">
            <span className="qms-doc-lib__review-duration">{review.primary}</span>
            {review.secondary ? (
              <>
                <span className="qms-doc-lib__review-sep">·</span>
                <span className="qms-doc-lib__review-due-date">{review.secondary}</span>
              </>
            ) : null}
          </div>
        </div>
      );
    }
    if (field === "IsReadingMandatory" || field === "IsQuizApplicable") {
      return formatYesNo(row[field]);
    }
    if (field === "PublishedOn" || field === "CompletedOn") {
      return String(row[field] || "—");
    }
    return String(row[field] ?? "—");
  }, []);

  const serverPagination = useMemo(
    () => ({
      page: pageNumber,
      pageSize,
      totalCount,
      loading: tableLoading,
      onPageChange: setPageNumber,
      onPageSizeChange: (size) => {
        setPageNumber(1);
        setPageSize(size);
      },
    }),
    [pageNumber, pageSize, totalCount, tableLoading],
  );

  const staffDisplayName = [
    userData?.FirstName || userData?.firstName,
    userData?.LastName || userData?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim() || String(userData?.StaffName || userData?.FullName || "").trim();

  if (access.status === "loading") {
    return (
      <div className="qms-page qms-my-learnings">
        <PageBreadcrumb items={[{ label: "Home", path: "/dashboard" }, { label: screen.title }]} />
        <p className="text-muted mb-0">Loading…</p>
      </div>
    );
  }

  if (access.status === "denied") {
    return (
      <div className="qms-page qms-my-learnings">
        <PageBreadcrumb items={[{ label: "Home", path: "/dashboard" }, { label: screen.title }]} />
        <div className="qms-page__denied">{access.message}</div>
      </div>
    );
  }

  return (
    <div className="qms-page qms-my-learnings">
      <PageBreadcrumb items={[{ label: "Home", path: "/dashboard" }, { label: screen.title }]} />

      <div className="qms-my-learnings__stats">
        {statCards.map((card) => (
          <div
            key={card.label}
            className={`qms-my-learnings-stat is-${card.tone}`}
            title={card.label}
          >
            <span className="qms-my-learnings-stat__label">{card.label}</span>
            <strong className="qms-my-learnings-stat__value">{card.value}</strong>
          </div>
        ))}
      </div>

      <div className="qms-doc-lib__report-wrap qms-my-learnings__table-wrap">
        <ReportTable
          rows={rows}
          fieldOptions={FIELD_OPTIONS}
          defaultColumns={DEFAULT_COLUMNS}
          renderCell={renderCell}
          pageSizeOptions={[10, 25, 50, 100]}
          serverPagination={serverPagination}
          columnSearchMode="server"
          serverColumnSearch={{
            searchableFields: MY_LEARNINGS_SERVER_SEARCH_FIELDS,
            debounceMs: 400,
            onChange: handleServerColumnSearch,
          }}
          renderColumnSearch={renderColumnSearch}
          persistLayoutKey={staffId ? `qms-my-learnings-layout-v2-${staffId}` : ""}
        />
      </div>

      <QmsMyLearningsWorkspaceModal
        open={Boolean(workspaceRow)}
        row={workspaceRow}
        userData={userData}
        staffDisplayName={staffDisplayName}
        onClose={() => setWorkspaceRow(null)}
        onCompleted={() => setRefreshToken((token) => token + 1)}
        notifyError={notifyError}
        notifySuccess={notifySuccess}
      />

      <InfoAlert {...alertState} onClose={hideAlert} />
    </div>
  );
}

export default QmsMyLearnings;
